/**
 * Contrato dos orçamentos: propostas de fornecedores e os itens de cada uma.
 *
 * Papel: aqui ficam `STATUS_ORCAMENTO`, `CATEGORIA_ORCAMENTO` e
 * `MODO_PAGAMENTO_ORCAMENTO`, de onde nascem os três enums do Postgres,
 * `totalDoOrcamento`, `proximoNumero` e os schemas que o formulário e as
 * Functions usam.
 *
 * Como usar: o **total não é campo**. É `max(Σ itens.valor − desconto, 0)`, e
 * quem o calcula é `totalDoOrcamento`. Guardá-lo numa coluna criaria um segundo
 * lugar onde a mesma verdade estaria escrita.
 *
 * O item **não tem quantidade**, e essa é a diferença central em relação ao
 * pedido de compra: aqui o valor de cada linha é o valor cheio dela, e a soma é
 * direta. Um fornecedor orça o serviço inteiro, não o preço de uma unidade.
 */
import { z } from 'zod';

import { dataSchema, idSchema, textoObrigatorio, textoOpcional } from './comum';
import { dinheiroComPadraoZeroSchema, dinheiroSchema } from './formatos';

/**
 * Em que ponto está a proposta.
 *
 * `orcamento` é onde ela nasce e onde volta ao ser duplicada; os outros três são
 * desfechos. `cancelado` e `nao_aprovado` são diferentes de propósito: o
 * primeiro é a família desistindo, o segundo é a proposta sendo recusada — e
 * saber qual dos dois foi muda se vale pedir de novo ao mesmo fornecedor.
 */
export const STATUS_ORCAMENTO = [
  'orcamento',
  'aprovado',
  'nao_aprovado',
  'cancelado',
] as const;

/**
 * A natureza do que está sendo orçado.
 *
 * `reforma` e `manutencao` são vizinhas e não se confundem: reforma muda o que a
 * casa é, manutenção devolve a casa ao que ela era. Quem for procurar quanto se
 * gastou para conservar a propriedade quer a segunda sem a primeira.
 *
 * `outros` fica por último porque é onde cai o que não coube, e por isso ele
 * conta junto a proposta que foi examinada e a que ninguém classificou: é o
 * valor de onde `ORCAMENTO_EM_BRANCO` parte e o padrão da coluna no banco.
 * `categoriaOrcamentoSchema` **não** tem `.default`, de modo que a chave ausente
 * é recusada em vez de virar `outros` em silêncio.
 */
export const CATEGORIA_ORCAMENTO = [
  'reforma',
  'manutencao',
  'evento',
  'servico',
  'outros',
] as const;

/** Como a proposta seria paga. */
export const MODO_PAGAMENTO_ORCAMENTO = ['a_vista', 'parcelado'] as const;

export const statusOrcamentoSchema = z.enum(STATUS_ORCAMENTO, {
  message: 'Situação desconhecida.',
});

export const categoriaOrcamentoSchema = z.enum(CATEGORIA_ORCAMENTO, {
  message: 'Categoria desconhecida.',
});

export const modoPagamentoSchema = z.enum(MODO_PAGAMENTO_ORCAMENTO, {
  message: 'Modo de pagamento desconhecido.',
});

export type StatusOrcamento = z.infer<typeof statusOrcamentoSchema>;
export type CategoriaOrcamento = z.infer<typeof categoriaOrcamentoSchema>;
export type ModoPagamentoOrcamento = z.infer<typeof modoPagamentoSchema>;

/**
 * O total de um orçamento, com o desconto abatido.
 *
 * O piso em zero é regra, e não defesa: um desconto maior que a soma dos itens
 * **não** vira crédito a favor da família. É o mesmo piso de `valorFinalDoItem`,
 * em Compras, e pelo mesmo motivo — num orçamento, desconto maior que o total é
 * engano de digitação.
 *
 * @param itens - Os itens da proposta, cada um com o valor cheio da linha.
 * @param desconto - O desconto sobre o conjunto.
 * @returns O total, nunca negativo.
 */
export function totalDoOrcamento(
  itens: readonly { valor: number }[],
  desconto: number,
): number {
  const soma = itens.reduce((total, item) => total + item.valor, 0);
  return Math.max(soma - desconto, 0);
}

/**
 * O próximo número a sugerir, no formato `NNN/AAAA`.
 *
 * Conta quantos orçamentos já existem naquele ano e soma um. **Não busca o maior
 * número**, e a diferença aparece quando alguém apaga: com `001` e `002`
 * gravados, apagar o `002` faz o próximo voltar a ser `002`. O número devolvido
 * é sugestão para um campo editável, e não identidade — dois orçamentos podem
 * terminar com o mesmo, e quem precisar de outro escreve outro.
 *
 * @param numerosExistentes - Os números já gravados, em qualquer ordem.
 * @param ano - O ano com quatro dígitos.
 * @returns O número sugerido, com três dígitos e o ano.
 */
export function proximoNumero(numerosExistentes: readonly string[], ano: number): string {
  const sufixo = `/${String(ano)}`;
  const doAno = numerosExistentes.filter((numero) => numero.endsWith(sufixo)).length;
  return `${String(doAno + 1).padStart(3, '0')}${sufixo}`;
}

/** A residência do orçamento, como a listagem a traz aninhada. */
export const residenciaDoOrcamentoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/**
 * A imagem de um item, com três estados que dizem coisas diferentes.
 *
 * Ausente significa **não mexa**: o formulário de edição não carrega as imagens,
 * e um corpo sem `foto` não pode apagar a que está gravada. String vazia ou nulo
 * significam **apague**. Uma string com conteúdo substitui.
 *
 * É o mesmo contrato que o item de compra tem, e pela mesma razão: sem os três
 * estados, salvar um orçamento apagaria as fotos que ninguém pediu para apagar.
 *
 * Exportado porque `formulario-orcamento.tsx` valida o arquivo escolhido com o
 * mesmo schema antes de gravá-lo no campo do item — recusar no navegador, com a
 * mesma mensagem, evita um `handleSubmit` que falha sem explicação porque o
 * arquivo passou dos 4.000.000 de caracteres em base64 (uns 3 MB de imagem).
 */
export const fotoDeEntradaSchema = z
  .string()
  .max(4_000_000, { message: 'Imagem grande demais.' })
  .nullish();

/**
 * O que o formulário envia para criar ou alterar um item do orçamento.
 *
 * `referencia` é o código com que o fornecedor identifica a peça: texto livre,
 * que não ordena, não conta e não identifica a linha. Quem procura o
 * identificador do item está atrás de `id`, e quem procura a ordem, de
 * `posicao`.
 */
export const novoItemOrcamentoSchema = z.object({
  /**
   * O item que esta linha continua sendo, quando ela já existia.
   *
   * É por ele que a imagem sobrevive à regravação. Casar por posição parece
   * bastar e não basta: remover a primeira linha faz a segunda assumir a posição
   * zero, e ela herdaria a imagem da que saiu.
   */
  id: idSchema.optional(),
  descricao: textoObrigatorio('Descrição', 300),
  referencia: textoOpcional(80),
  valor: dinheiroSchema,
  foto: fotoDeEntradaSchema,
});

export type NovoItemOrcamento = z.infer<typeof novoItemOrcamentoSchema>;

/**
 * O que o formulário envia para criar ou alterar um orçamento.
 *
 * `fornecedor` é a única obrigação: um orçamento sem item nenhum é válido, e é
 * assim que se registra a proposta que foi pedida e ainda não chegou.
 *
 * `categoria` é obrigatória mesmo tendo padrão na coluna: um corpo sem a chave
 * responde 400, e não `outros`. A diferença aparece em quem chama a API sem
 * passar pelo formulário — ali o esquecimento é apontado, em vez de virar uma
 * proposta classificada por ninguém.
 *
 * Não há campo de total: ele é `totalDoOrcamento` sobre os itens e o desconto.
 */
export const novoOrcamentoSchema = z.object({
  residenciaId: idSchema,
  numero: textoObrigatorio('Número', 40),
  data: dataSchema,
  status: statusOrcamentoSchema,
  categoria: categoriaOrcamentoSchema,
  fornecedor: textoObrigatorio('Fornecedor', 200),
  responsavel: textoOpcional(200),
  solicitante: textoOpcional(200),
  pagamento: modoPagamentoSchema,
  desconto: dinheiroComPadraoZeroSchema,
  observacao: textoOpcional(2000),
  itens: z.array(novoItemOrcamentoSchema).default([]),
});

export type NovoOrcamento = z.infer<typeof novoOrcamentoSchema>;

/** Um item como a API o devolve, sem a imagem. */
export const itemOrcamentoSchema = z.object({
  id: idSchema,
  posicao: z.number().int(),
  descricao: z.string(),
  referencia: z.string().nullable(),
  valor: z.number(),
  /** Se há imagem guardada; o conteúdo dela vem por rota própria. */
  temFoto: z.boolean(),
});

export type ItemOrcamento = z.infer<typeof itemOrcamentoSchema>;

/** Um orçamento como a API o devolve, com os itens aninhados. */
export const orcamentoSchema = z.object({
  id: idSchema,
  residencia: residenciaDoOrcamentoSchema,
  numero: z.string(),
  data: z.string(),
  status: statusOrcamentoSchema,
  categoria: categoriaOrcamentoSchema,
  fornecedor: z.string(),
  responsavel: z.string().nullable(),
  solicitante: z.string().nullable(),
  pagamento: modoPagamentoSchema,
  desconto: z.number(),
  observacao: z.string().nullable(),
  itens: z.array(itemOrcamentoSchema),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Orcamento = z.infer<typeof orcamentoSchema>;

/** A imagem de um item, como a rota que a devolve a entrega. */
export const fotoDoItemOrcamentoSchema = z.object({
  id: idSchema,
  foto: z.string().nullable(),
});
